import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { combineLatest, map, Observable, take } from 'rxjs';
import { Tag } from 'src/app/services/api';
import { AppState, LoadTags } from 'src/app/state';
import { AddTagsToProduct, LoadTagsOfProduct, PostTag, ProductState, RemoveTagsFromProduct } from '../../product/state';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { SubscriptionHandlerComponent } from '../subscription-handler/subscription-handler.component';

interface NewlyCreatedTag
{
  id: number;
  name: string;
  description: string;
  isNewlyCreated: boolean;
}

interface AddTagToProductDialogData
{
  productId: number;
}

@Component({
  selector: 'app-add-tag-to-product-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatCheckboxModule,
    MatChipsModule,
    MatTooltipModule,
  ],
  templateUrl: './add-tag-to-product-dialog.component.html',
  styleUrl: './add-tag-to-product-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class AddTagToProductDialogComponent extends SubscriptionHandlerComponent implements OnInit
{
  tagsOfProduct$: Observable<Tag[] | undefined> = this.store.select(ProductState.tags);
  tags$: Observable<Tag[]> = this.store.select(AppState.tags);

  tagControl = new FormControl({ value: '', disabled: false });
  descriptionControl = new FormControl({ value: '', disabled: false }, [Validators.required, Validators.minLength(15)]);
  filteredTags$!: Observable<Tag[]>;

  productId: number | null = null;

  selectedTags: (Tag | NewlyCreatedTag)[] = [];

  // Initial tags when dialog opens
  private originalProductTags: Tag[] = [];

  // Tags to add/remove to the product
  tagsToAdd: Tag[] = [];
  tagsToRemove: Tag[] = [];

  // Backup of description when control is disabled
  currentDescriptionText: string = '';

  lastSelectedTagId: string | null = null; // For highlighting selected tag
  newlyCreatedTags: NewlyCreatedTag[] = []; // Tags created in UI but not saved to backend yet

  constructor(
    private store: Store,
    private dialogRef: MatDialogRef<AddTagToProductDialogComponent>,
    private dialog: MatDialog,
    private actions$: Actions,
    @Inject(MAT_DIALOG_DATA) private dialogData: AddTagToProductDialogData
  )
  {
    super();
    this.productId = this.dialogData.productId;
  }

  // Format tag name for autocomplete display
  displayFn(tag: Tag | null): string
  {
    return tag?.name || '';
  }

  ngOnInit()
  {
    this.store.dispatch(new LoadTags());
    if (this.productId)
      this.store.dispatch(new LoadTagsOfProduct(this.productId));

    // Load and initialize product tags
    this.subscribeTo(this.tagsOfProduct$, tags =>
    {
      if (tags)
      {
        // Create a new array to ensure change detection
        this.originalProductTags = Array.isArray(tags) ? [...tags] : [];
        this.updateSelectedTagsArray();
      }
    });

    // Disable description input initially if needed
    if (this.isDescriptionDisabled())
    {
      this.descriptionControl.disable();
    }

    // Handle tag input changes to manage description field state
    this.subscribeTo(this.tagControl.valueChanges, () =>
    {
      if (this.isDescriptionDisabled())
      {
        this.descriptionControl.disable();
        this.descriptionControl.reset();
      }
      else
      {
        this.descriptionControl.enable();
      }
    });

    // Set up tag filtering for autocomplete
    this.filteredTags$ = combineLatest([
      this.tags$,
      this.tagControl.valueChanges
    ]).pipe(
      map(([tags, filterValue]) =>
      {
        // Show no options when input is empty
        if (!filterValue)
        {
          return [];
        }

        // Handle both string input and tag object selection
        let filter = '';
        if (filterValue)
        {
          filter = typeof filterValue === 'string'
            ? filterValue.toLowerCase()
            : (filterValue as any).name?.toLowerCase() || '';
        }

        return tags.filter(tag => tag.name?.toLowerCase().includes(filter));
      })
    );
  }

  onCancel(): void
  {
    this.confirmClose();
  }

  onSubmit(): void
  {
    if (this.productId)
    {
      // Track all actions to be dispatched
      const actionsToDispatch: any[] = [];

      // Handle existing tags
      const tagIdsToAdd = this.tagsToAdd.map(tag => tag.id!);
      const tagIdsToRemove = this.tagsToRemove.map(tag => tag.id!);

      // Create new tags if needed
      if (this.newlyCreatedTags.length > 0)
      {
        for (const tag of this.newlyCreatedTags)
        {
          actionsToDispatch.push(new PostTag(this.productId, tag.name, tag.description));
        }
      }

      // Add tags to product if needed
      if (tagIdsToAdd.length > 0)
      {
        actionsToDispatch.push(new AddTagsToProduct(this.productId, tagIdsToAdd));
      }

      // Remove tags from product if needed
      if (tagIdsToRemove.length > 0)
      {
        actionsToDispatch.push(new RemoveTagsFromProduct(this.productId, tagIdsToRemove));
      }

      // If there are actions to dispatch, handle their completion
      if (actionsToDispatch.length > 0)
      {
        // Create set of action types to wait for
        const actionTypes = new Set([
          PostTag.type,
          AddTagsToProduct.type,
          RemoveTagsFromProduct.type
        ].filter(type =>
          actionsToDispatch.some(action => action instanceof PostTag && type === PostTag.type) ||
          actionsToDispatch.some(action => action instanceof AddTagsToProduct && type === AddTagsToProduct.type) ||
          actionsToDispatch.some(action => action instanceof RemoveTagsFromProduct && type === RemoveTagsFromProduct.type)
        ));

        const typesToWatch = Array.from(actionTypes);
        const actionsCount = actionsToDispatch.length;

        // Dispatch all actions
        actionsToDispatch.forEach(action => this.store.dispatch(action));

        // Track action completion
        this.subscribeTo(
          this.actions$.pipe(
            ofActionCompleted(
              ...typesToWatch.map(type => ({ type }))
            ),
            take(actionsCount)
          ),
          () =>
          {
            // On the last action completion, close the dialog
            if (--actionsToDispatch.length === 0)
            {
              this.dialogRef.close();
            }
          }
        );
      }
      else 
      {
        // No actions to wait for, close immediately
        this.dialogRef.close();
      }
    }
  }

  // Rebuilds the selectedTags array with current tag selections
  private updateSelectedTagsArray(): void
  {
    // First, directly include originalProductTags that are not in tagsToRemove
    const selectedOriginalTags = this.originalProductTags.filter(
      tag => tag?.id && !this.tagsToRemove.some(t => t.id === tag.id)
    );

    // Add any tags that are in tagsToAdd
    const additionalTags = this.tagsToAdd.filter(tag =>
      !selectedOriginalTags.some(t => t.id === tag.id)
    );

    // Create a new array for the selected tags
    this.selectedTags = [...selectedOriginalTags, ...additionalTags, ...this.newlyCreatedTags];
  }

  isTagSelected(tag: Tag): boolean
  {
    const inOriginalTags = this.originalProductTags.some(t => t.id === tag.id);
    const inTagsToAdd = this.tagsToAdd.some(t => t.id === tag.id);
    const inTagsToRemove = this.tagsToRemove.some(t => t.id === tag.id);
    const inNewlyCreatedTags = this.newlyCreatedTags.some(t => t.name?.toLowerCase() === tag.name?.toLowerCase());

    return (inOriginalTags && !inTagsToRemove) || inTagsToAdd || inNewlyCreatedTags;
  }

  toggleTagSelection(tag: Tag | NewlyCreatedTag, checked: boolean, shouldClearInput: boolean = true): void
  {
    if (this.isNewTag(tag))
    {
      const isCurrentlyDisplayedTag = this.lastSelectedTagId &&
        this.lastSelectedTagId === tag.id?.toString();

      // Remove tag from newlyCreatedTags collection
      this.newlyCreatedTags = this.newlyCreatedTags.filter(t => t.id !== tag.id);
      this.updateSelectedTagsArray();

      // Clear input if needed or if removing currently displayed tag
      if (shouldClearInput || isCurrentlyDisplayedTag)
      {
        this.clearInput();
      }

      // Reset selection state if removing displayed tag
      if (isCurrentlyDisplayedTag)
      {
        this.lastSelectedTagId = null;
      }

      return;
    }

    // Handle existing tags
    const existingTag = tag as Tag;
    const inOriginalTags = this.originalProductTags.some(t => t.id === existingTag.id);

    if (checked)
    {
      // Adding a tag
      if (!inOriginalTags)
      {
        // Add to tagsToAdd for new selections
        if (!this.tagsToAdd.some(t => t.id === existingTag.id))
        {
          this.tagsToAdd.push(existingTag);
        }
      } else
      {
        // Restore previously removed tag
        this.tagsToRemove = this.tagsToRemove.filter(t => t.id !== existingTag.id);
      }
    } else
    {
      // Removing a tag
      if (inOriginalTags)
      {
        // Mark original tag for removal
        if (!this.tagsToRemove.some(t => t.id === existingTag.id))
        {
          this.tagsToRemove.push(existingTag);
        }
      } else
      {
        // Remove from added tags
        this.tagsToAdd = this.tagsToAdd.filter(t => t.id !== existingTag.id);
      }
    }

    // Refresh selected tags
    this.updateSelectedTagsArray();

    // Clear input field after selection if requested
    if (shouldClearInput)
    {
      this.clearInput();
    }
  }

  // Process tag selection from autocomplete dropdown
  onTagSelected(event: MatAutocompleteSelectedEvent): void
  {
    const selectedTag = event.option.value as Tag;

    // Add the tag without clearing input
    this.toggleTagSelection(selectedTag, true, false);

    // Display tag name in input
    this.tagControl.setValue(selectedTag.name || '');

    // Set description if available
    if (selectedTag.description)
    {
      if (typeof selectedTag.description === 'object' && 'initialText' in selectedTag.description)
      {
        const descText = selectedTag.description.initialText || '';

        this.descriptionControl.clearValidators();
        this.descriptionControl.updateValueAndValidity();

        this.currentDescriptionText = descText;
        this.descriptionControl.setValue(descText, { emitEvent: false });
      }
      else if (typeof selectedTag.description === 'string')
      {
        const descText = selectedTag.description || '';

        this.descriptionControl.clearValidators();
        this.descriptionControl.updateValueAndValidity();

        this.currentDescriptionText = descText;
        this.descriptionControl.setValue(descText, { emitEvent: false });
      }
    }

    // Track selected tag for highlighting
    this.lastSelectedTagId = selectedTag.id ? selectedTag.id.toString() : null;

    // Backup description value
    this.currentDescriptionText = this.descriptionControl.value || '';

    // Disable inputs without triggering events
    this.tagControl.disable({ emitEvent: false, onlySelf: true });
    this.descriptionControl.disable({ emitEvent: false, onlySelf: true });
  }

  confirmClose(): void
  {
    if (this.hasChanges())
    {
      // There are changes -> show confirm dialog
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        data: {
          title: 'Unsaved Changes',
          message: 'You have unsaved tag changes. Are you sure you want to close without applying them?',
          confirmButtonText: 'Yes, Close',
          cancelButtonText: 'No, Keep Editing'
        }
      });

      this.subscribeTo(confirmDialog.afterClosed(), result =>
      {
        if (result === true)
        {
          this.dialogRef.close();
        }
      });
    } else
    {
      this.dialogRef.close();
    }
  }

  clearInput(event?: Event): void
  {
    this.tagControl.setValue('');
    this.descriptionControl.setValue('');
    this.currentDescriptionText = '';

    this.tagControl.enable();
    this.descriptionControl.enable();

    // Remove highlight from selected tag
    this.lastSelectedTagId = null;

    // Restore validators for the description control when creating new tags
    this.descriptionControl.setValidators([Validators.required, Validators.minLength(15)]);
    this.descriptionControl.updateValueAndValidity();

    // Only stop propagation if we're handling an actual event
    if (event)
    {
      event.stopPropagation();
    }
  }

  // Clears selection when clicking on the tags container background
  clearSelection(event: MouseEvent): void 
  {
    const target = event.target as HTMLElement;
    const container = event.currentTarget as HTMLElement;

    // Skip only if clicked on a chip
    if (target.closest('mat-chip'))
    {
      return;
    }

    // If we clicked anywhere in the container (including section-label),
    // clear the selection
    if (target.closest('.tag-chip-set') ||
      target === container ||
      target.closest('.section-label') ||
      target.closest('.selected-tags-container') === container)
    {
      // Clear the input fields and enable them
      this.tagControl.setValue('');
      this.tagControl.enable();
      this.descriptionControl.setValue('');
      this.descriptionControl.enable();
      this.currentDescriptionText = '';

      // Remove highlight from selected tag
      this.lastSelectedTagId = null;

      // Restore validators
      this.descriptionControl.setValidators([Validators.required, Validators.minLength(15)]);
      this.descriptionControl.updateValueAndValidity();
    }
  }

  // Gets the current description text, whether the control is enabled or disabled
  get currentDescription(): string
  {
    return this.descriptionControl.disabled ? this.currentDescriptionText : (this.descriptionControl.value || '');
  }

  // Handle clicking on a tag chip to view or unselect it
  viewTagDetails(tag: Tag | NewlyCreatedTag): void
  {
    const tagId = tag.id?.toString() || null;

    // If the tag is already selected, unselect it
    if (this.lastSelectedTagId === tagId)
    {
      // Clear selection
      this.lastSelectedTagId = null;
      this.tagControl.setValue('');
      this.descriptionControl.setValue('');
      this.currentDescriptionText = '';

      // Enable controls
      this.tagControl.enable();
      this.descriptionControl.enable();

      // Restore validators
      this.descriptionControl.setValidators([Validators.required, Validators.minLength(15)]);
      this.descriptionControl.updateValueAndValidity();
      return;
    }

    // Otherwise, select the new tag
    this.lastSelectedTagId = tagId;

    this.tagControl.setValue(tag.name || '', { emitEvent: false });
    let descriptionValue = '';

    // Set the description if available
    if ('description' in tag && tag.description)
    {
      if (typeof tag.description === 'object' && 'initialText' in tag.description)
      {
        descriptionValue = tag.description.initialText || '';
      } else if (typeof tag.description === 'string')
      {
        descriptionValue = tag.description || '';
      }
    }
    this.descriptionControl.setValue(descriptionValue, { emitEvent: false });

    // patchValue to ensure Angular's change detection picks it up
    this.descriptionControl.patchValue(descriptionValue, { emitEvent: false });

    this.tagControl.disable({ emitEvent: false, onlySelf: true });
    this.descriptionControl.disable({ emitEvent: false, onlySelf: true });
  }

  createTag(): void
  {
    if (this.tagControl.value && this.descriptionControl.valid)
    {
      // Extract the tag name based on input type
      let newTagName: string;
      if (typeof this.tagControl.value === 'string')
      {
        newTagName = this.tagControl.value.trim();
      } else
      {
        const tagValue = this.tagControl.value as any;
        newTagName = tagValue?.name?.trim() || '';
      }

      const tagDescription = this.descriptionControl.value;

      // Create a temporary tag object with a negative ID to distinguish from existing tags
      const tempId = -(this.newlyCreatedTags.length + 1);

      // Create a new tag object using our NewlyCreatedTag interface
      const newTag: NewlyCreatedTag = {
        id: tempId, // Temporary negative ID
        name: newTagName,
        description: tagDescription || '',
        isNewlyCreated: true
      };

      this.newlyCreatedTags.push(newTag);

      this.updateSelectedTagsArray();

      this.currentDescriptionText = this.descriptionControl.value || '';

      this.tagControl.disable({ emitEvent: false, onlySelf: true });
      this.descriptionControl.disable({ emitEvent: false, onlySelf: true });

      // Highlight the newly created tag
      this.lastSelectedTagId = tempId.toString();
    }
  }


  //Checks if there are any changes to the tags (additions, removals, or newly created tags)
  hasChanges(): boolean
  {
    return this.tagsToAdd.length > 0 || this.tagsToRemove.length > 0 || this.newlyCreatedTags.length > 0;
  }

  //Checks if the description input should be disabled
  isDescriptionDisabled(): boolean
  {
    if (!this.tagControl.value)
    {
      return true;
    }

    let inputValue: string;

    if (typeof this.tagControl.value === 'string')
    {
      inputValue = this.tagControl.value.toLowerCase().trim();
    } else
    {
      const tagValue = this.tagControl.value as any;
      inputValue = tagValue?.name?.toLowerCase().trim() || '';
    }

    const allTags = this.store.selectSnapshot(AppState.tags);

    // Disable if tag with same name already exists
    return allTags.some(tag => tag.name?.toLowerCase() === inputValue);
  }

  // Checks if the current input value exists as a tag already or if the description is invalid
  isCreateButtonDisabled(): boolean
  {
    if (!this.tagControl.value || !this.descriptionControl.valid)
    {
      return true;
    }

    let inputValue: string;

    if (typeof this.tagControl.value === 'string')
    {
      inputValue = this.tagControl.value.toLowerCase().trim();
    } else
    {
      const tagValue = this.tagControl.value as any;
      inputValue = tagValue?.name?.toLowerCase().trim() || '';
    }

    const allTags = this.store.selectSnapshot(AppState.tags);

    // Check if tag with same name already exists
    return allTags.some(tag => tag.name?.toLowerCase() === inputValue);
  }

  trackByTagId(index: number, tag: Tag | NewlyCreatedTag): number
  {
    return tag.id || index;
  }

  //Safely check if a tag is newly created
  isNewTag(tag: Tag | NewlyCreatedTag): boolean
  {
    return 'isNewlyCreated' in tag && tag.isNewlyCreated === true;
  }

  // Check if a tag is in the tagsToAdd array
  isAddedTag(tag: Tag | NewlyCreatedTag): boolean
  {
    // Only works for tags with IDs (not newly created ones)
    if (!tag.id) return false;
    return this.tagsToAdd.some(t => t.id === tag.id);
  }
}
